import React, { useEffect, useState } from 'react';

import ibanValidator from 'iban';
import { useTranslation } from 'react-i18next';

import { bemCssModules, getTestId } from '@utils';
import { useNavigate } from '@utils/hooks';

import { accountsActions, globalActions, notificationsActions, useStore } from '@modules';
import { PhoneFormatsType } from '@modules/global/types';
import { NotificationTypesEnum } from '@modules/notifications/types';
import { VerifyNextEnum } from '@modules/types';

import Button from '@components/Button';
import Captcha from '@components/Captcha';
import Input from '@components/InputOld';

import PurposeOfPayment from './PurposeOfPayment';
import RecentTransfers from './RecentTransfers';
import RecepientCountry from './RecepientCountry';
import { useCustomContext } from '../context';

import style from './Form.scss';

const cn = bemCssModules(style, 'Form');
const ti = getTestId('Form');

export type TForm = {
  isSubmitDisabled?: boolean;
};

const Form: CustomFC<TForm> = ({ className, isSubmitDisabled }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [{ accounts, global }, dispatch] = useStore();
  const { context, setContext } = useCustomContext();
  const [error, setError] = useState<string>();
  const { isShowCaptcha, captcha } = global;

  const [ibanCountries, setIbanCountries] =
    useState<{ value: PhoneFormatsType; label: string }[]>();
  const { iban, swift, recipient, country, city, address, purpose, amount } = context;
  const { iban: sender_iban, iban_currency } = accounts;

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setContext(draft => {
      draft[e.target.name] = e.target.value;
    });
  };

  useEffect(() => {
    dispatch(globalActions.getIbanCountries())
      .unwrap()
      .then(res => {
        const formated = res.data.map(obj => ({ value: obj.code, label: obj.title }));
        setIbanCountries(formated);
      });
  }, []);

  useEffect(() => {
    setError(undefined);
  }, [iban]);

  const onSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (isShowCaptcha) dispatch(globalActions.setShowCaptcha(false));

    if (!ibanValidator.isValid(iban)) {
      setError(t('accounts.invalid_iban_error'));
      return;
    }

    const country_code =
      ibanCountries?.find(el => el.label === country)?.value || CONST.defaultCountryCode;

    const data = {
      amount,
      sender_iban: sender_iban || '',
      full_name: recipient,
      swift,
      country_code,
      city,
      address,
      currency: iban_currency,
      purpose,
      captcha,
      beneficiary_iban: iban,
    };

    dispatch(accountsActions.accountWithdraw(data))
      .unwrap()
      .then(res => {
        const { next } = res.data;
        if (next === VerifyNextEnum.verifyPhone || next === VerifyNextEnum.verifyEmail) {
          navigate<'accountsSendVerification'>(CONST.nav.accountsSendVerification, {
            state: { next },
          });
        }
      })
      .catch(() => {
        const notificationData = {
          title: t('notifications.payment_declined.title'),
          message: t('notifications.payment_declined.message'),
          type: NotificationTypesEnum.error,
          canHide: false,
        };
        dispatch(notificationsActions.add(notificationData));
      });
  };

  const isLoading = accounts.loaders.accountWithdraw || accounts.loaders.getAccountEstimateFee;
  const isDisabled =
    !amount || !purpose || !iban || !swift || !recipient || !country || isSubmitDisabled;

  return (
    <form className={cn(null, [className])} data-testid={ti()} onSubmit={onSubmit}>
      <RecentTransfers countries={ibanCountries} error={error} />
      <Input
        className={cn('swift-input')}
        label={t('public.swift_code')}
        name="swift"
        placeholder={t('sepa.swift_code_placeholder')}
        onChange={handleChange}
        value={swift}
        autoComplete="off"
      />
      <Input
        label={t('sepa.recipient')}
        name="recipient"
        placeholder={t('sepa.recipient_placeholder')}
        onChange={handleChange}
        value={recipient}
        autoComplete="off"
      />
      <RecepientCountry countries={ibanCountries} />
      <Input
        label={t('sepa.recipient_city')}
        name="city"
        placeholder={t('sepa.recipient_city')}
        onChange={handleChange}
        value={city}
        autoComplete="off"
      />
      <Input
        label={t('sepa.recipient_address')}
        name="address"
        placeholder={t('sepa.recipient_address')}
        onChange={handleChange}
        value={address}
        autoComplete="off"
      />
      <PurposeOfPayment className={cn('purpose-input')} />
      {isShowCaptcha && <Captcha className={cn('captcha')} />}

      <Button className={cn('submit')} type="submit" isLoading={isLoading} disabled={isDisabled}>
        {t('operations.send')} {CONST.accountsCurrency}
      </Button>
    </form>
  );
};

export default Form;
